import type { ConditionValueValidator } from '@pushly/aqe'
import { deriveJourneyState } from './journey-status-tree'

/**
 * The completeness rules segmentation supplies, keyed the way the builder resolves them:
 * `field.inputKey ?? field.type`, the same key the field's value input and row summary are
 * registered under.
 *
 * These state what the builder cannot work out for itself. Its own rules judge a value against the
 * shape the engine reads it as, and they run first — anything they can settle is theirs. What is
 * left is a composite value whose parts mean something only to the control that wrote it, and the
 * clearest case is a nested inner query: the builder can see the inner rules are present, but not
 * that one of them stands in for a choice the reader has not made. It could not tell generically
 * either, because an unmade choice and a real comparison reach the wire in the same shape.
 */
export const SEGMENT_VALUE_VALIDATORS: Record<string, ConditionValueValidator> = {
    'journey-status': validateJourneyStatus,
    'keyword-clicked': requireExpression('Select a keyword'),
    'page-tag-visited': requireExpression('Select a page tag'),
    'page-url-visited': requireExpression('Enter a URL'),
}

/**
 * A count-in-a-window condition that says what it is counting.
 *
 * "At least three" is not a condition until it says three of what, and the missing half rides in
 * `meta.expression` rather than in the value — so the builder's own rules read the row as complete
 * and the collapsed row reads as a finished sentence with a clause simply absent.
 *
 * Registered per row rather than checked only on submit. The submit check can name the field but not
 * the row, which in a segment with several groups leaves the reader comparing a message against every
 * row to find the one it means — and an incomplete row looks exactly like a complete one, both showing
 * a count and a window. Registered here, the row marks itself and Save says so while the reader is
 * still in the editor that can fix it.
 *
 * The submit check stays as the backstop it always was: it also catches a document loaded from storage
 * whose rows the reader never opened.
 */
function requireExpression(message: string): ConditionValueValidator {
    return ({ meta }) => {
        const named = typeof meta?.expression === 'string' ? meta.expression.trim() : ''

        return named ? null : message
    }
}

/**
 * A journey-status condition with every choice its controls ask for actually made.
 *
 * Naming a specific journey or a trigger and then not picking one leaves a rule comparing against
 * nothing — a shape the engine reads as a comparison to null rather than as an unfinished edit. The
 * rule is still written into the tree rather than dropped, which is what makes this check the thing
 * that reports it: dropping it would leave a tree reading back as some other choice entirely, moving
 * the reader's selection under them in place of telling them what is left to fill in.
 *
 * A journey with no status chosen needs no rule here. That is the "entered" match — in the journey
 * whatever its status — and the tree carries the journey rule alone, which is exactly what the
 * engine evaluates it as.
 */
function validateJourneyStatus(context: { value: unknown }): string | null {
    const state = deriveJourneyState(context.value)

    if (state.selectionType === 'specific' && state.journeyId === undefined) {
        return 'Select a journey'
    }

    if (state.selectionType === 'triggered' && state.triggerType === undefined) {
        return 'Select a trigger'
    }

    return null
}
